import React from 'react';
import { motion } from 'framer-motion';
import { Scissors, PenTool, Ruler, Clock, Users, Sparkles, Heart } from 'lucide-react';

const CustomDesign = () => {
  const process = [
    {
      icon: <PenTool className="h-8 w-8 text-emerald-400" />,
      title: "Initial Consultation",
      description: "Discuss your vision, preferences, and requirements in detail."
    },
    {
      icon: <Ruler className="h-8 w-8 text-emerald-400" />,
      title: "Measurements & Fitting",
      description: "Precise measurements and initial fitting to ensure perfect fit."
    },
    {
      icon: <Scissors className="h-8 w-8 text-emerald-400" />,
      title: "Design & Creation",
      description: "Expert craftsmanship brings your unique design to life."
    },
    {
      icon: <Sparkles className="h-8 w-8 text-emerald-400" />,
      title: "Final Touches",
      description: "Meticulous attention to detail in finishing your garment."
    }
  ];

  const showcase = [
    {
      title: "Evening Gown",
      image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Bespoke evening wear for special occasions."
    },
    {
      title: "Business Attire",
      image: "https://images.unsplash.com/photo-1485230895905-ec40ba36b9bc?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Custom tailored suits and professional wear."
    },
    {
      title: "Special Occasions",
      image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Unique designs for memorable moments."
    },
    {
      title: "Casual Luxury",
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      description: "Elevated everyday wear tailored to you."
    }
  ];

  const benefits = [
    {
      icon: <Users className="h-12 w-12 text-emerald-400" />,
      title: "Personal Attention",
      description: "One-on-one consultations with our expert designers."
    },
    {
      icon: <Clock className="h-12 w-12 text-emerald-400" />,
      title: "Timeless Quality",
      description: "Garments crafted to last and cherish for years."
    },
    {
      icon: <Heart className="h-12 w-12 text-emerald-400" />,
      title: "Perfect Fit",
      description: "Tailored precisely to your measurements and preferences."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1558769132-cb1aea458c5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Custom Design</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Bespoke fashion crafted exclusively for you
            </p>
          </motion.div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Design Process</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From concept to creation, we ensure every detail is perfect
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-400/10 mb-6">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-[2px] bg-emerald-400/20"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Custom Creations</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A glimpse into our bespoke designs and craftsmanship
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {showcase.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl"
              >
                <div className="aspect-w-16 aspect-h-9">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-6">
                  <div>
                    <h3 className="text-2xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-300">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Custom Design</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Experience the luxury of perfectly tailored fashion
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 rounded-2xl border border-gray-800 hover:border-emerald-400/50 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-400/10 mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <PenTool className="h-16 w-16 text-emerald-400 mx-auto mb-8" />
          <h2 className="text-4xl font-bold mb-6">Start Your Custom Journey</h2>
          <p className="text-gray-400 mb-8">
            Let's create something unique together. Book a consultation with our design team and bring your vision to life.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-8 py-3 bg-emerald-400 text-black font-semibold rounded-full hover:bg-emerald-300 transition-colors"
          >
            Schedule Design Consultation
          </motion.button>
        </div>
      </section>
    </motion.div>
  );
};

export default CustomDesign; 